import { useState } from 'react';
import { TFunction } from 'react-i18next';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Typography from '../../../componentsv2/Typography';
import Section from '../../../componentsv2/Section';
import Chip from '../../../componentsv2/Chip';
import MoreMenu from '../../../componentsv2/MoreMenu';
import Button from '../../../componentsv2/Button';
import TwoFactorSetupModal from '../../../componentsv2/TwoFactoSetupModal';
import GenerateRecoveryCodeModal from '../../../componentsv2/GenerateRecoveryCodeModal';
import { useAuth } from '../../../hooks';
import { twoFactorEvents } from '../../../constants/events';
import { COLORS } from '../../../themev2';

export default function SecurityTab({ t }: { t: TFunction }) {
  const { user } = useAuth();
  const [showTwoFactorSetupModal, setShowTwoFactorSetupModal] = useState(false);
  const [showRecoveryCodeModal, setShowRecoveryCodeModal] = useState(false);

  const handleOnClickSetup = () => {
    logEvent(twoFactorEvents.CLICK_SET_UP_TWO_FACTOR_FROM_SECURITY_TAB);
    setShowTwoFactorSetupModal(true);
  };

  const handleOnClickGenerateRecoveryCode = () => {
    logEvent(twoFactorEvents.CLICK_GENERATE_RECOVERY_CODE_FROM_SECURITY_TAB);
    setShowRecoveryCodeModal(true);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography sx={{ fontWeight: 600, fontSize: '18px', lineHeight: '27px', color: COLORS.gray[1400] }}>
          {t('security.title')}
        </Typography>
        <Typography sx={{ fontWeight: 400, fontSize: '14px', lineHeight: '21px', color: COLORS.gray[1100] }}>
          {t('security.description')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Section>
          <Grid container>
            <Grid item xs={12} md={10} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: COLORS.gray[1200],
                }}
              >
                {t('security.googleAuthenticatorTitle')}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: COLORS.gray[1100],
                }}
              >
                {t('security.googleAuthenticatorDescription')}
              </Typography>
            </Grid>
            {user?.tfaEnabled ? (
              <Grid
                item
                xs={12}
                md={2}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
              >
                <Chip label={t('security.enabled')} sx={{ backgroundColor: '#E1FAED', color: '#11705F' }} />
                <MoreMenu items={[]} disabled />
              </Grid>
            ) : (
              <Grid item xs={12} md={2}>
                <Button color="secondary" fullWidth type="outline" onClick={handleOnClickSetup}>
                  {t('security.setup')}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={10} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: COLORS.gray[1200],
                }}
              >
                {t('security.recoveryCodeTitle')}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: COLORS.gray[1100],
                }}
              >
                {t('security.recoveryCodeDescription')}
              </Typography>
            </Grid>
            <Grid item xs={2} md={2}>
              <Button
                color="secondary"
                fullWidth
                type="outline"
                onClick={handleOnClickGenerateRecoveryCode}
                disabled={!user?.tfaEnabled}
              >
                {t('security.recoveryCodeButton')}
              </Button>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <TwoFactorSetupModal
        isOpen={showTwoFactorSetupModal}
        handleClose={() => {
          logEvent(twoFactorEvents.CLICK_CLOSE_MODAL);
          setShowTwoFactorSetupModal(false);
        }}
        onSuccess={() => {
          setShowTwoFactorSetupModal(false);
        }}
      />
      <GenerateRecoveryCodeModal
        isOpen={showRecoveryCodeModal}
        handleClose={() => {
          logEvent(twoFactorEvents.CLICK_CLOSE_GENERATE_RECOVERY_CODE_MODAL);
          setShowRecoveryCodeModal(false);
        }}
      />
    </Grid>
  );
}
